import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CreateDeviceSchema, type FormValues } from '../validators';
import { useMutation } from '@apollo/client';
import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { CREATE_DEVICE } from '../graphql/mutations/createDevice';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const defaultType = 'Tcore';
const defaultIntegrationType = 'I-L';

export const useCreateDeviceForm = ({ onClose }: { onClose: () => void }) => {
  const { _ } = useLingui();
  const { setModalState: setHardwareModal } = useGlobalModal('saveHardwareConfigurationModal');
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const { control, watch, setValue, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(CreateDeviceSchema),
    defaultValues: {
      isIntegration: false,
      type: defaultType,
    },
  });

  const [mutate, { loading }] = useMutation(CREATE_DEVICE, {
    onCompleted(data) {
      setHardwareModal({
        open: true,
        deviceId: data.createDevice.id,
      });
      const profile = data.createDevice.profile;
      openModalSuccessMessage(
        _(msg`Dispositivo ${profile.name} con ID: ${profile.id} creado con éxito`),
      );
      onClose();
    },
    onError(error) {
      openModalErrorMessage(_(msg`Hubo en error al crear dispositivo`) + ': ' + error.message);
    },
  });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'isIntegration') {
        if (value.isIntegration) {
          setValue('type', defaultIntegrationType, { shouldTouch: true });
        } else {
          setValue('type', defaultType, { shouldTouch: true });
        }
      }

      if (name === 'type') {
        setValue('children', []);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const onSubmit = useMemo(
    () =>
      handleSubmit(async (data) => {
        if (loading) return;

        await mutate({
          variables: {
            input: {
              name: data.name,
              type: data.typeEnum,
              sensorVersion: data.sensorVersion,
              organizationId: data.organizationId,
              irrigationId: data.irrigationId,
              integrationId: data.integrationId,
              location: data.location,
              children: data.children,
              shares: data.shares,
            },
          },
        });
      }),
    [handleSubmit, mutate, loading],
  );

  return {
    control,
    watch,
    onSubmit,
    formState,
  };
};
